import React, { useState, useContext, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { getVerbalColors } from 'helpers'
import { Typography, Switch, Skeleton, Alert } from 'foundations-library/components'
import Spinner from 'spinner'
import { UserContext } from 'providers'
import API from 'users-app/src/util/api_v5'
import { Plan, CallSummary, EmailNotifications } from '../Index'

const useStyles = makeStyles(theme => {
    const { colors } = theme['foundations-library']
    const verbalColors = getVerbalColors(colors)

    return {
        toggleItem: {
            display: 'flex',
            padding: '1px',
            flexDirection: 'column',
            alignItems: 'flex-start',
            alignSelf: 'stretch',
            borderRadius: '6px',
            border: `1px solid ${verbalColors['neutral-100']}`
        },
        toggleContainer: {
            display: 'flex',
            padding: '15px',
            alignItems: 'center',
            gap: '20px',
            alignSelf: 'stretch',
            borderRadius: '6px',
            background: '#FFF'
        },
        textsContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            flex: '1 0 0'
        },
        switchContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            width: 50
        }
    }
})

interface ToggleItemProps {
    title: string
    subTitle: string
    enabled: boolean
    type: typeof CallSummary.type | typeof EmailNotifications.type
    switchEnabled: boolean
    setSwitchEnabled: React.Dispatch<React.SetStateAction<boolean>>
    isOtherToggleEnabled: boolean
    setOtherToggleEnabled: React.Dispatch<React.SetStateAction<boolean>>
    disabled: boolean
    loadingTheState: boolean
}

/**
 *
 */
const ToggleItem = ({ title, subTitle, enabled, type, loadingTheState, switchEnabled, setSwitchEnabled, isOtherToggleEnabled, setOtherToggleEnabled, disabled }: ToggleItemProps) => {
    const classes = useStyles()
    const [errorMessage, setErrorMessage] = useState<null | string>(null)
    const [isLoading, setIsLoading] = useState(false)
    const userInfo = useContext(UserContext)

    const isAdmin = userInfo?.role === 'account'
    const plan = userInfo?.user_account_type.toLowerCase() as Plan

    const isSwitchEnabledForNonAdminProUser = plan === Plan.pro && !isAdmin && switchEnabled

    const handleSwitchChange = async (isEnabled: boolean) => {
        try {
            setErrorMessage(null)
            setIsLoading(true)

            const payload: Record<string, any> = { voip_phone_id: userInfo.extension_id }
            if (type === CallSummary.type) {
                payload.call_summaries = isEnabled
            } else if (type === EmailNotifications.type) {
                payload.email_notifications = isEnabled
            }
            await API.updateUserCallSummaryPreferences(payload)
            setSwitchEnabled(isEnabled)
            if (type === CallSummary.type) setOtherToggleEnabled(false)
        } catch (error) {
            console.error('Failed to update call summary preferences:', error)
            setErrorMessage((error as Record<string, any>)?.errors?.call_summaries || (error as Record<string, any>)?.errors?.email_notifications || (error as Error)?.message)
        } finally {
            setIsLoading(false)
        }
    }

    const renderSkeleton = (width, height, margin = '0') => <Skeleton variant='text' width={width} height={height} margin={margin} />

    useEffect(() => {
        setSwitchEnabled(enabled)
    }, [enabled])

    return (
        <div className={classes.toggleItem}>
            <div className={classes.toggleContainer}>
                <div className={classes.textsContainer}>
                    {
                        !loadingTheState &&
                        <>
                            <Typography variant='subtitle2' color={!disabled ? 'neutral-900' : 'neutral-300'}>{title}</Typography>
                            <Typography variant='body3' color={!disabled ? 'neutral-600' : 'neutral-300'}>
                                {subTitle}
                            </Typography>
                            {errorMessage && <Alert level='error' onClose={() => setErrorMessage(null)}>{errorMessage}</Alert>}
                        </>
                    }
                    {
                        loadingTheState &&
                        (
                            <>
                                {renderSkeleton(100, 15, '0 0 5px 0')}
                                {renderSkeleton(250, 15)}
                            </>
                        )
                    }
                </div>
                <div className={classes.switchContainer}>
                    {
                        !loadingTheState &&
                        <>
                            <Switch checked={switchEnabled} disabled={isLoading || (type === CallSummary.type && isSwitchEnabledForNonAdminProUser) || (type === EmailNotifications.type && !isOtherToggleEnabled) || disabled} onChange={handleSwitchChange} value={switchEnabled} />
                            {isLoading && <Spinner color='neutral' size={'small'} />}
                        </>
                    }
                    {
                        loadingTheState &&
                        renderSkeleton(30, 20)
                    }
                </div>
            </div>
        </div>
    )
}

export default ToggleItem
